import { z } from 'zod';

import { OhlcCoverageSchema, OhlcSeriesSchema } from './ohlc-series.schema';

/**
 * The `GET /v1/benchmark/ohlc` contract (ADR-0045 decisions J and K ·
 * `plans-cc/technical-scorecard-ohlcv-plan.md` §1.11). Every bare `§` below
 * names that feature plan, not the authority plan.
 *
 * SEPARATE FROM `ohlc-series.schema.ts` on a real seam, not to satisfy a line
 * gate. `OhlcSeries` is the payload shape shared by BOTH OHLC routes;
 * everything here — the unavailable vocabulary, the symbol enum, the result
 * union — is benchmark-only, so `/v1/securities/:ticker/ohlc` has no reason to
 * import any of it. The dependency runs one way: this file imports the series
 * contract and nothing imports back.
 */

/**
 * Why a benchmark OHLC window carries no series. A CLOSED set on a **200**
 * union arm — expected unavailability is not an error code (ADR-0045
 * reject-list): a benchmark with only close-only history is a normal state of
 * the world, and a 404/500 would make `HttpRepository` treat it as a fault.
 *
 * `ComparisonUnavailableReason` is a different, `core`-only vocabulary and
 * must never appear here — `ohlc_unavailable` is a comparison verdict, not a
 * route outcome.
 *
 * ADDING A MEMBER is a client-breaking change on a `private, max-age=300`
 * route: a client holding a fresh cached body never revalidates, so its
 * `z.enum` fails the new value — the same hazard this file's header cites for
 * not widening `PriceSeries`. Neither OHLC route registers a
 * `representationVersion` today, so that mitigation is absent. A new member
 * must register one in the same commit.
 */
export const OHLC_UNAVAILABLE_REASONS = [
  'no_complete_ohlc',
  'empty_window',
  'not_in_served_set',
] as const;
export const OhlcUnavailableReasonSchema = z.enum(OHLC_UNAVAILABLE_REASONS);
export type OhlcUnavailableReason = z.infer<typeof OhlcUnavailableReasonSchema>;

/**
 * The closed benchmark symbol vocabulary for `GET /v1/benchmark/ohlc`
 * (§1.11 hop 7: "an explicit, Zod-validated symbol selector over a closed
 * enum, not an open string").
 *
 * MEMBERSHIP IS NOT SERVING. This enum is the set of symbols the wire
 * RECOGNISES; the served set is runtime state in `market.benchmark_served` and
 * is `{SPY}` until the Stage 6 promotion admits QQQ. So `symbol=QQQ` is
 * schema-valid and returns `200` `not_in_served_set`, while a symbol outside
 * this enum is `400 VALIDATION_FAILED` (§6.6). Collapsing the two would make
 * the closed reason `not_in_served_set` unreachable.
 */
export const BENCHMARK_SYMBOLS = ['SPY', 'QQQ'] as const;
export const BenchmarkSymbolSchema = z.enum(BENCHMARK_SYMBOLS);
export type BenchmarkSymbol = z.infer<typeof BenchmarkSymbolSchema>;

/**
 * `GET /v1/benchmark/ohlc` body and the `getBenchmarkOhlcSeries` return.
 *
 * BOTH arms are HTTP 200 with `ApiResponse` success. Unavailable means the
 * complete-OHLC suffix is EMPTY — not that the window endpoint is incomplete
 * (that case serves a truncated suffix and compares as `endpoint_mismatch` in
 * `core`). `coverage` survives on the unavailable arm so a caller can say what
 * was looked at, and is `null` when there was nothing to cover.
 *
 * ATTRIBUTION IS THE CALLER'S KEY, deliberately. The available arm identifies
 * itself through `series.ticker`/`series.window`; the unavailable arm carries
 * no `symbol` or `window`, so a parsed result cannot say on its own which
 * benchmark it describes. That is not an omission to fix here: §6.6 and §1.11
 * both fix this arm as `{ status, reason, coverage }`, and §1.11 hop 8 already
 * puts `symbol`, `asOf` and `window` in the Query key and the ETag `routeKey`,
 * so every caller holds the attribution it would otherwise read back off the
 * payload. Widening the arm is available as a plan §6.6 + §1.11 table change
 * with its own §11 criterion — not a change this schema may make unilaterally,
 * because the plan wins on conflict.
 */
/**
 * The reasons under which NOTHING can have been examined, so §6.6's "null when
 * there was nothing to cover" is the only representable answer. `empty_window`
 * means the operational window held no sessions; `not_in_served_set` means the
 * symbol is schema-valid but unadmitted, so no window was looked at at all.
 * Only `no_complete_ohlc` — sessions existed, none complete — can carry a
 * coverage object, which is why it is absent from this set.
 */
const COVERAGE_FREE_REASONS: readonly OhlcUnavailableReason[] = [
  'empty_window',
  'not_in_served_set',
];

export const BenchmarkOhlcResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('available'), series: OhlcSeriesSchema }),
  z
    .object({
      status: z.literal('unavailable'),
      reason: OhlcUnavailableReasonSchema,
      coverage: OhlcCoverageSchema.nullable(),
    })
    .superRefine((val, ctx) => {
      /* Without this, one body could assert both that the window was empty and
         that five sessions were covered — and Stage 3 and Stage 6 could each
         pick a different reading of `coverage` and both parse. */
      if (val.coverage === null || !COVERAGE_FREE_REASONS.includes(val.reason)) return;
      ctx.addIssue({
        code: 'custom',
        message: `reason '${val.reason}' examined nothing, so coverage must be null`,
        path: ['coverage'],
      });
    }),
]);
export type BenchmarkOhlcResult = z.infer<typeof BenchmarkOhlcResultSchema>;
